import Grid from '@mui/material/Grid';
import { FormProvider, useForm } from 'react-hook-form';
import { VmsAccount, updatePassword } from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsColors,
  WsDialog,
  WsDialogSize,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { failed, success } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';

const gridStyle = {
  m: 2,
};

const textFieldInputProps = {
  style: {
    padding: '2px',
    height: '30px',
    maxWidth: '100%',
    marginLeft: '10px',
  },
};

export interface IUpdatePasswordDialog {
  open: boolean;
  handleClose: () => void;
  selectedVmsAccount: VmsAccount;
  getCurrentVmsAccount: () => VmsAccount;
}

interface UpdatePasswordFormData {
  id: string;
  accountName: string;
  confirmAccountPassword: string;
  accountPassword: string;
}

const UpdatePasswordDialog = ({
  open,
  handleClose,
  selectedVmsAccount,
  getCurrentVmsAccount,
}: IUpdatePasswordDialog) => {
  const methods = useForm<UpdatePasswordFormData>({
    defaultValues: {
      accountName: selectedVmsAccount.accountName,
    },
  });
  const setNotification = useNotificationStore((state) => state.setNotification);
  const { handleSubmit } = methods;
  const onUpdatePassword = async (data: UpdatePasswordFormData) => {
    const vmsDto: VmsAccount = {
      ...getCurrentVmsAccount(),
      accountPassword: methods.getValues('confirmAccountPassword'),
    };

    try {
      if (data.accountPassword === data.confirmAccountPassword) {
        const response = await updatePassword(vmsDto);
        if (response.status === 200) {
          setNotification(success('Password Updated Successfully!'));
          setTimeout(() => handleClose(), 1800);
        }
      } else {
        setNotification(failed('Password mismatch! Please check your password and try again.'));
        setTimeout(() => handleClose(), 1800);
      }
    } catch (e) {
      setNotification(failed('Failed to Update Password!'));
      setTimeout(() => handleClose(), 1800);
    }
  };

  return (
    <WsDialog openDialog={open} onClose={handleClose} size={WsDialogSize.SMALL}>
      <FormProvider {...methods}>
        <Grid
          component="form"
          xs={12}
          bgcolor={WsColors.White}
          p={3}
          onSubmit={handleSubmit(onUpdatePassword)}
        >
          <Grid xs={12}>
            <Grid
              container
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={1}
              mb={2}
              xs={12}
            >
              <WsTypography variant={WsTypoVariants.TITLE} bold>
                Update Password
              </WsTypography>
            </Grid>
          </Grid>
          <Grid container sx={gridStyle}>
            <Grid
              xs={5}
              direction="row"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              pr={2}
            >
              <WsTypography bold variant={WsTypoVariants.BODY1}>
                Account Name:
              </WsTypography>
            </Grid>
            <Grid xs={7} display="flex" justifyContent="center" alignItems="center">
              <ControlledTextInput
                name="accountName"
                id="account-name"
                variant="outlined"
                focused
                required
                fullWidth={false}
                inputProps={textFieldInputProps}
              />
            </Grid>
          </Grid>

          <Grid container sx={gridStyle}>
            <Grid
              item
              xs={5}
              direction="row"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              pr={2}
            >
              <WsTypography bold variant={WsTypoVariants.BODY1}>
                Enter New Password:
              </WsTypography>
            </Grid>
            <Grid item xs={7} display="flex" justifyContent="center" alignItems="center">
              <ControlledTextInput
                name="accountPassword"
                size="small"
                id="vms-accountPassword"
                variant="outlined"
                focused
                type="password"
                required
                inputProps={textFieldInputProps}
              />
            </Grid>
          </Grid>

          <Grid container sx={gridStyle}>
            <Grid
              item
              xs={5}
              direction="row"
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              pr={2}
            >
              <WsTypography bold variant={WsTypoVariants.BODY1}>
                Confirm New Password:
              </WsTypography>
            </Grid>
            <Grid item xs={7} display="flex" justifyContent="center" alignItems="center">
              <ControlledTextInput
                name="confirmAccountPassword"
                size="small"
                id="vms-accountPassword"
                variant="outlined"
                focused
                type="password"
                required
                inputProps={textFieldInputProps}
              />
            </Grid>
          </Grid>

          <Grid
            container
            direction="column"
            alignItems="center"
            justifyContent="center"
            sx={gridStyle}
            pt={2}
          >
            <Grid container item xs={12} columnGap={2} justifyContent="center">
              <Grid item>
                <WsButton variant="outlined" size="medium" onClick={() => handleClose()}>
                  Cancel
                </WsButton>
              </Grid>
              <Grid item>
                <WsButton variant="contained" size="medium" type="submit">
                  Save
                </WsButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </FormProvider>
    </WsDialog>
  );
};
export default UpdatePasswordDialog;
